import { ICurrentSubscriptionResponse, IPlanDetails } from '@zeta-types';

type SubscriptionCatalogPlan = {
  planId?: string;
  planName?: string;
  planType?: string;
  displayOrder?: number;
  activeCategories?: string[];
};

const isValidSubscriptionPlan = (plan?: IPlanDetails | null): plan is IPlanDetails => {
  return Boolean(plan && (plan.planName || plan.licenceReferenceID));
};

export const getSubscriptionPlans = (subscription?: ICurrentSubscriptionResponse | null): IPlanDetails[] => {
  return Array.isArray(subscription?.plans) ? subscription.plans.filter(isValidSubscriptionPlan) : [];
};

export const hasActiveSubscriptionPlans = (subscription?: ICurrentSubscriptionResponse | null): boolean => {
  return getSubscriptionPlans(subscription).length > 0;
};

export const getPrimarySubscriptionPlan = (subscription?: ICurrentSubscriptionResponse | null): IPlanDetails | undefined => {
  const activePlans = getSubscriptionPlans(subscription);
  if (!activePlans.length) return undefined;

  const assignedPlan = activePlans.find((plan) => plan.licenceReferenceID === subscription?.assignedPlanReferenceID);
  return assignedPlan || activePlans[0];
};

const normalizePlanName = (value?: string): string =>
  (value || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const getCanonicalPlanKey = (value?: string): 'enterprise' | 'discovery' | 'flexible' | null => {
  const normalized = normalizePlanName(value).replace(/ plan$/, '');

  if (
    normalized.includes('enterprise')
    || normalized.includes('paraplanner')
  ) {
    return 'enterprise';
  }

  if (
    normalized.includes('discovery')
    || normalized === 'ifa'
    || normalized.includes('ifa')
  ) {
    return 'discovery';
  }

  if (
    normalized.includes('flexible')
    || normalized.includes('flex plan')
    || normalized.includes('wealth manager')
  ) {
    return 'flexible';
  }

  return null;
};

const planPriority = (plan?: { planName?: string; planType?: string; displayOrder?: number } | null): number => {
  const name = normalizePlanName(plan?.planName);
  const type = normalizePlanName(plan?.planType);

  if (name.includes('enterprise') || name.includes('paraplanner')) return 3;
  if (name.includes('discovery') || name === 'ifa' || name.includes('ifa')) return 2;
  if (name.includes('flex') || type === 'flexible_plan') return 1;

  return 0;
};

export const getHighestRankedSubscriptionPlan = (
  subscription?: ICurrentSubscriptionResponse | null,
  availablePlans: SubscriptionCatalogPlan[] = []
): SubscriptionCatalogPlan | null => {
  const activePlans = getSubscriptionPlans(subscription);
  if (!activePlans.length || !availablePlans.length) return null;

  const assignedPlanId = subscription?.assignedPlanReferenceID;
  if (assignedPlanId) {
    const assignedCatalogPlan = availablePlans.find((availablePlan) => availablePlan.planId === assignedPlanId);
    if (assignedCatalogPlan) {
      return assignedCatalogPlan;
    }
  }

  const matchedPlans = activePlans
    .map((plan) => {
      const byId = availablePlans.find((availablePlan) => availablePlan.planId === plan.licenceReferenceID);
      if (byId) return byId;

      const byName = availablePlans.find((availablePlan) => normalizePlanName(availablePlan.planName) === normalizePlanName(plan.planName));
      if (byName) return byName;

      const canonicalPlanKey = getCanonicalPlanKey(plan.planName);
      if (!canonicalPlanKey) return null;

      return (
        availablePlans.find((availablePlan) => getCanonicalPlanKey(availablePlan.planName) === canonicalPlanKey)
        || null
      );
    })
    .filter((plan): plan is SubscriptionCatalogPlan => Boolean(plan));

  if (!matchedPlans.length) return null;

  return matchedPlans.sort((left, right) => {
    const priorityDiff = planPriority(right) - planPriority(left);
    if (priorityDiff !== 0) return priorityDiff;

    const displayOrderDiff = (right.displayOrder ?? Number.NEGATIVE_INFINITY) - (left.displayOrder ?? Number.NEGATIVE_INFINITY);
    if (displayOrderDiff !== 0) return displayOrderDiff;

    return normalizePlanName(right.planName).localeCompare(normalizePlanName(left.planName));
  })[0];
};

export const getHighestRankedSubscriptionPlanLabel = (
  subscription?: ICurrentSubscriptionResponse | null,
  availablePlans: SubscriptionCatalogPlan[] = [],
  fallback = 'Subscription'
): string => {
  const highestPlan = getHighestRankedSubscriptionPlan(subscription, availablePlans);
  return highestPlan?.planName || getSubscriptionPlanLabel(subscription, fallback);
};

export const getSubscriptionPlanIds = (subscription?: ICurrentSubscriptionResponse | null): string[] => {
  return getSubscriptionPlans(subscription)
    .map((plan) => plan.licenceReferenceID)
    .filter((planId): planId is string => Boolean(planId));
};

export const hasSubscriptionPlanId = (subscription?: ICurrentSubscriptionResponse | null, planId?: string | null): boolean => {
  return Boolean(planId) && getSubscriptionPlanIds(subscription).includes(planId);
};

export const getSubscriptionPlanNames = (subscription?: ICurrentSubscriptionResponse | null): string[] => {
  return getSubscriptionPlans(subscription)
    .map((plan) => plan.planName)
    .filter((planName): planName is string => Boolean(planName?.trim()));
};

export const getSubscriptionPlanLabel = (subscription?: ICurrentSubscriptionResponse | null, fallback = 'Subscription'): string => {
  const planNames = getSubscriptionPlanNames(subscription);
  return planNames.length ? planNames.join(', ') : fallback;
};
